import { cva, type VariantProps } from 'class-variance-authority';
import {
  disabledState,
  focusRing,
  transitionBase,
  uiMarker,
  uiMarkerLayout,
} from './shared';

/**
 * PICKER MENU
 *
 * The Urgently screens' own dropdown: a bordered trigger with a chevron, and
 * a list of options that drops open *in place* beneath it rather than in a
 * portal. Six of them were written by hand — the cancel and GOA reasons, the
 * two completion questions, the driver assignment and the status change —
 * and each spelled the same trigger, the same floating panel and the same
 * "selected row is tinted" rule with its own classes and two inline colours.
 *
 * It is not a Select: it is uncontrolled by the keyboard, it does not close
 * on an outside click, and the screen owns its open state. Those are the
 * behaviours the screens had, kept; a screen that wants the real thing
 * should reach for Select.
 */

/** The wrapper: what the menu is positioned against. */
export const pickerMenuRecipe = cva([uiMarker, uiMarkerLayout, 'relative'], {
  variants: {
    width: {
      full: 'w-full',
      /** The status control on a call sheet: a fixed, narrow column. */
      sm: 'w-48',
    },
  },
  defaultVariants: { width: 'full' },
});

/** The closed control. Input-shaped, with the ring held while it is open. */
export const pickerTriggerRecipe = cva(
  [
    uiMarker,
    'flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-background px-4 py-3 text-left font-sans text-body text-foreground',
    transitionBase,
    'hover:bg-muted/50',
    focusRing,
    disabledState,
  ],
  {
    variants: {
      open: {
        true: 'ring-focus ring-ring',
        false: '',
      },
    },
    defaultVariants: { open: false },
  }
);

/** The chevron on the trigger, turned over while the menu is open. */
export const pickerChevronRecipe = cva(
  [
    uiMarker,
    'size-icon-md shrink-0 text-muted-foreground transition-transform duration-fast',
  ],
  {
    variants: {
      open: {
        true: 'rotate-180',
        false: '',
      },
    },
    defaultVariants: { open: false },
  }
);

/** The open panel, dropped under the trigger. */
export const pickerMenuListRecipe = cva(
  [
    uiMarker,
    'absolute left-0 top-full z-50 mt-2 flex w-full flex-col overflow-auto rounded-lg border border-border bg-background p-2 shadow-e3',
  ],
  {
    variants: {
      maxHeight: {
        sm: 'max-h-60',
        md: 'max-h-72',
        lg: 'max-h-80',
        none: '',
      },
    },
    defaultVariants: { maxHeight: 'md' },
  }
);

/** One row of the panel. The chosen one is tinted. */
export const pickerOptionRecipe = cva(
  [
    uiMarker,
    'flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left font-sans text-body-sm-strong text-foreground',
    transitionBase,
    'hover:bg-muted/50',
    focusRing,
    disabledState,
  ],
  {
    variants: {
      selected: {
        true: 'bg-muted',
        false: 'bg-background',
      },
    },
    defaultVariants: { selected: false },
  }
);

/**
 * The frame an option sits in when it carries a hint: `group` is what lets
 * the hint show on hover, and `relative` is what it is positioned against.
 */
export const pickerOptionFrameRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'group relative',
]);

/**
 * A note shown above an option while the pointer is over it — "a truck is
 * required" on a driver that cannot be assigned yet. Inverted like Tooltip,
 * but drawn in place: the panel is itself a floating layer, so a portal would
 * only be a second one.
 */
export const pickerOptionHintRecipe = cva([
  uiMarker,
  'pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-foreground px-2 py-1 font-sans text-caption text-background group-hover:block',
]);

export type PickerMenuRecipeProps = VariantProps<typeof pickerMenuRecipe>;
export type PickerMenuListRecipeProps = VariantProps<
  typeof pickerMenuListRecipe
>;
export type PickerOptionRecipeProps = VariantProps<typeof pickerOptionRecipe>;
